import React from 'react';
import styled from 'styled-components';
import { withTranslation } from 'react-i18next';
import Filter from 'common/components/molecules/Filter';
import FilterItem_T from 'common/components/atoms/FilterItem';
import MsgContainer from 'common/components/atoms/MsgContainer';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { toastr } from 'react-redux-toastr';
import * as _ from 'lodash';

import { canAccessOrgDashboard } from 'common/rbac/util';
import { userActions } from '../../onboarding/user.actions';
import { orgContextActions } from '../Organisation/orgContext';
import { isOrgDashboardRoute, getOrgDashboardPath, getOrgIdFromPath } from '../Organisation/orgRouteUtils';
import { history } from '../../common/helpers';

const FilterItem = styled(FilterItem_T)`
  min-width: 200px;
  font-size: 13px;
`;

export const OrgHeader = styled.div`
  display: flex;
  align-items: center;
  min-width: 200px;
  padding: 12px 12px 6px 12px;
  font-weight: ${props => (props.selected ? 700 : 600)};
  font-size: 13px;
  color: ${props => (props.selected ? '#2f3441' : '#9aa0a9')};
  cursor: ${props => (props.clickable ? 'pointer' : 'default')};
  user-select: none;

  &:hover {
    color: ${props => (props.clickable ? '#1a73e8' : props.selected ? '#2f3441' : '#9aa0a9')};
  }
  position: relative;

  &::before {
    content: '';
    display: inline-block;
    width: 12px;
    border-top: 1px solid #d0d4db;
    margin-right: 8px;
    flex-shrink: 0;
  }
`;

export const SiteRow = styled(FilterItem_T)`
  min-width: 200px;
  padding-left: 44px;
  position: relative;
  font-size: 13px;

  &::before {
    content: '';
    position: absolute;
    left: 18px;
    top: 0;
    bottom: 0;
    border-left: 1px solid #d0d4db;
  }

  &.last-site::before {
    bottom: 50%;
  }

  &::after {
    content: '';
    position: absolute;
    left: 18px;
    top: 50%;
    width: 16px;
    border-top: 1px solid #d0d4db;
  }
`;

function buildFlatItems(accounts) {
  return (accounts || [])
    .slice()
    .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
    .map(account => ({
      type: 'flat',
      text: account.name,
      value: account.id,
      key: `flat-${account.id}`
    }));
}

class SitesDropdown extends React.Component {
  constructor() {
    super();
    this.state = {
      dropdownOpen: false,
      accounts: [],
      accountSearchKey: ''
    };
  }

  componentDidMount() {
    this.loadAccountList();
  }

  loadAccountList = () => {
    const { user } = this.props.user;
    const accounts = user ? user.accounts : [];
    const backendHierarchy = user && user.account_switcher && Array.isArray(user.account_switcher.items)
      ? user.account_switcher.items
      : null;
    const list = backendHierarchy && backendHierarchy.length > 0
      ? backendHierarchy
      : buildFlatItems(accounts);
    this.setState({ accounts: list, orgHierarchy: list });
  };

  componentWillReceiveProps(nextProps) {
    const { accountSelectionState } = nextProps;
    const prevAccountSelectionState = this.props.accountSelectionState;

    if (accountSelectionState.result && prevAccountSelectionState.loadingId) {
      history.push('/');
    } else if (accountSelectionState.result === false) {
      toastr.error(accountSelectionState.error);
    }
  }

  componentDidUpdate(prevProps) {
    if (!_.isEqual(prevProps.discardAnalystSummary, this.props.discardAnalystSummary) && this.props.discardAnalystSummary) {
      const { selectAccount } = this.props;

      if (this.pendingOrgNavigation) {
        const item = this.pendingOrgNavigation;
        this.pendingOrgNavigation = null;
        this.completeOrgNavigation(item);
        this.props.setDiscardAnalystSummary(false);
        return;
      }

      if (this.lastAccount && this.lastAccount !== this.props.user.account_id) {
        selectAccount(this.lastAccount);
        this.props.setDiscardAnalystSummary(false);
      }
    }

    if (prevProps.analystSummaryWarning && !this.props.analystSummaryWarning && !this.props.discardAnalystSummary) {
      this.pendingOrgNavigation = null;
      this.lastAccount = null;
    }

    const prevAccounts = prevProps.user.user ? prevProps.user.user.accounts : [];
    const currAccounts = this.props.user.user ? this.props.user.user.accounts : [];
    const prevHierarchy = prevProps.user.user ? prevProps.user.user.account_switcher : null;
    const currHierarchy = this.props.user.user ? this.props.user.user.account_switcher : null;
    if (!_.isEqual(prevAccounts, currAccounts) || !_.isEqual(prevHierarchy, currHierarchy)) {
      this.loadAccountList();
    }
  }

  getSelectedAccount = () => {
    const { user } = this.props.user;
    const accounts = user && user.accounts ? user.accounts : [];
    const selectedAccount = accounts.filter(
      account => account.id === (user ? user.account_id : null)
    )[0];
    return selectedAccount;
  }

  isOrgViewActive = () => isOrgDashboardRoute(history.location.pathname);

  completeOrgNavigation = (item) => {
    if (!item || !item.organisation_id) return;
    if (!canAccessOrgDashboard(this.props.user && this.props.user.user, item.organisation_id)) return;
    this.props.setOrgContext({ id: item.organisation_id, name: item.text });
    this.setState({ dropdownOpen: false });
    history.push(getOrgDashboardPath(item.organisation_id));
  };

  handleDropdownChange = (value) => {
    this.lastAccount = null;
    const selectedAccount = this.getSelectedAccount();

    if (this.isOrgViewActive()) {
      this.props.clearOrgContext();
      if (isOrgDashboardRoute(history.location.pathname)) {
        history.push('/');
      }
    }

    if (!selectedAccount || selectedAccount.id !== value) {
      const { selectAccount } = this.props;
      if (this.props.checkNewSummaryExists && this.props.checkNewSummaryExists()) {
        this.props.setAnalystSummaryWarning(true);
        this.lastAccount = value;
        return;
      }
      selectAccount(value);
    }
  };

  handleOrgHeaderClick = (item) => {
    if (!item || !item.organisation_id) return;
    if (!canAccessOrgDashboard(this.props.user && this.props.user.user, item.organisation_id)) return;
    if (this.props.checkNewSummaryExists && this.props.checkNewSummaryExists()) {
      this.pendingOrgNavigation = item;
      this.props.setAnalystSummaryWarning(true);
      return;
    }
    this.completeOrgNavigation(item);
  };

  searchAccountFilter = (value) => {
    const { orgHierarchy } = this.state;

    if (!value) {
      this.setState({ accounts: orgHierarchy, accountSearchKey: value });
      return;
    }

    const lowerValue = value.toLowerCase();
    const grouped = [];
    const flatMatches = [];
    let currentHeader = null;
    let matchingSites = [];

    for (let i = 0; i < orgHierarchy.length; i += 1) {
      const item = orgHierarchy[i];
      if (item.type === 'org-header') {
        if (currentHeader && matchingSites.length > 0) {
          grouped.push(currentHeader);
          matchingSites.forEach((site, idx) => {
            grouped.push({ ...site, lastInOrg: idx === matchingSites.length - 1 });
          });
        }
        currentHeader = item;
        matchingSites = [];
      } else if (item.type === 'site') {
        if (
          item.text.toLowerCase().includes(lowerValue) ||
          (currentHeader && currentHeader.text.toLowerCase().includes(lowerValue))
        ) {
          matchingSites.push(item);
        }
      } else if (item.type === 'flat') {
        if (item.text.toLowerCase().includes(lowerValue)) {
          flatMatches.push(item);
        }
      }
    }
    if (currentHeader && matchingSites.length > 0) {
      grouped.push(currentHeader);
      matchingSites.forEach((site, idx) => {
        grouped.push({ ...site, lastInOrg: idx === matchingSites.length - 1 });
      });
    }

    this.setState({ accounts: [...grouped, ...flatMatches], accountSearchKey: value });
  };

  renderFilterOptions = (options, name) => {
    if (_.isEmpty(options)) {
      return (
        <MsgContainer>
          <span>No {name} are available</span>
        </MsgContainer>
      );
    }

    const selectedAccount = this.getSelectedAccount();
    const isOrgView = this.isOrgViewActive();
    const activeOrgId = isOrgView ? getOrgIdFromPath(history.location.pathname) : null;
    const user = this.props.user && this.props.user.user;

    return options.map((item) => {
      if (item.type === 'org-header') {
        return (
          <OrgHeader
            key={item.key}
            onClick={() => this.handleOrgHeaderClick(item)}
            clickable={!!item.organisation_id && canAccessOrgDashboard(user, item.organisation_id)}
            selected={activeOrgId != null && item.organisation_id === activeOrgId}
          >
            {item.text}
          </OrgHeader>
        );
      }

      if (item.type === 'site') {
        const cls = `dropdown-item${item.lastInOrg ? ' last-site' : ''}`;
        return (
          <SiteRow
            key={item.key}
            className={cls}
            selected={!isOrgView && selectedAccount && selectedAccount.id === item.value}
            onClick={() => this.handleDropdownChange(item.value)}
          >
            <span className="text">{item.text}</span>
          </SiteRow>
        );
      }

      return (
        <FilterItem
          key={item.key || item.value}
          className="dropdown-item"
          selected={selectedAccount && selectedAccount.id === item.value}
          onClick={() => this.handleDropdownChange(item.value)}
        >
          <span className="text">{item.text}</span>
        </FilterItem>
      );
    });
  };

  toggleOpen = () => {
    this.setState(prevState => ({ dropdownOpen: !prevState.dropdownOpen }));
  };

  getDisplayAccountName = () => {
    if (this.isOrgViewActive()) {
      const routeOrgId = getOrgIdFromPath(history.location.pathname);
      const { orgContext } = this.props;
      if (orgContext && orgContext.id === routeOrgId && orgContext.name) {
        return orgContext.name;
      }
    }
    const selectedAccount = this.getSelectedAccount();
    const { t } = this.props;
    if (!selectedAccount || !selectedAccount.name) return '';
    if (selectedAccount.name.toLowerCase() === 'demo account') return t('topBar.demoAccount');
    return selectedAccount.name;
  };

  render() {
    const selectedAccount = this.getSelectedAccount();
    const isOrgView = this.isOrgViewActive();
    const displayName = this.getDisplayAccountName();
    return (
      <Filter
        id="SitesDropdown"
        value={displayName}
        open={this.state.dropdownOpen}
        toggleOpen={this.toggleOpen}
        onChangeSearch={this.searchAccountFilter}
        searchValue={this.state.accountSearchKey}
        selectedItems={isOrgView || !selectedAccount
          ? []
          : this.state.accounts.filter(acc => acc.value === selectedAccount.id)}
        customDisplayValue={() => (
          <span id="account-name" title={this.props.t('topBar.currentAccount')}>
            {this.getDisplayAccountName()}
          </span>
        )}
        zIndex={1000}
        itemName="account"
        marginTop="7px"
        modalClassName="accounts-list"
        displayItemClassName="display-item"
        autoFocusSearch
      >
        {this.renderFilterOptions(this.state.accounts, 'accounts')}
      </Filter>
    );
  }
}

const mapStateToProps = (state) => {
  const { user, orgContext } = state;
  return {
    user,
    orgContext,
    accountSelectionState: state.accountSelectionState
  };
};

const mapDispatchToProps = dispatch =>
  bindActionCreators(
    {
      selectAccount: userActions.selectAccount,
      setOrgContext: orgContextActions.setOrgContext,
      clearOrgContext: orgContextActions.clearOrgContext
    },
    dispatch
  );

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withTranslation()(SitesDropdown));
